import type { NexusEntryPoint, NexusIntegrationFlow } from './components/steps/IntegrationStep/Nexus/types';
import {
  BLOCKED_SUB_CASE,
  CF_NEXUS_FROM_STEPS,
  CF_SUB_CASES,
  type CfEventSubject,
  type CfNexusFromStep,
} from '../cloudflare-integration/cfVocabulary';

export const INTEGRATE_STEP_ENTRY_POINT: NexusEntryPoint = 'integrate_step';

const NEXUS_ENTRY_POINTS: readonly NexusEntryPoint[] = [INTEGRATE_STEP_ENTRY_POINT, 'verify_step'];

export const AUTOMATED_CF_INTEGRATION_FLOW: NexusIntegrationFlow = 'automated_cf';

/** The part of a CF opening that survives the move to the guides view in the page history. */
export type NexusReferralCfOpening = { fromStep: CfNexusFromStep; domain?: string; subject?: CfEventSubject };

export type NexusReferral = {
  nexusReferralCode: NexusEntryPoint;
  newConversation?: boolean;
  cfOpening?: NexusReferralCfOpening;
};

export type NexusReferralOptions = Omit<NexusReferral, 'nexusReferralCode'>;

export function nexusReferralState(
  entryPoint: NexusEntryPoint,
  { newConversation = false, cfOpening }: NexusReferralOptions = {},
): NexusReferral {
  return {
    nexusReferralCode: entryPoint,
    ...(newConversation && { newConversation }),
    ...(cfOpening && { cfOpening }),
  };
}

const isNexusEntryPoint = (value: unknown): value is NexusEntryPoint =>
  NEXUS_ENTRY_POINTS.includes(value as NexusEntryPoint);

const isCfNexusFromStep = (value: unknown): value is CfNexusFromStep =>
  CF_NEXUS_FROM_STEPS.includes(value as CfNexusFromStep);

const isString = (value: unknown): value is string => typeof value === 'string';

type CfSubjectSubCase = CfEventSubject['subCase'];

const CF_SUBJECT_SUB_CASES: readonly CfSubjectSubCase[] = [...CF_SUB_CASES, BLOCKED_SUB_CASE, null, undefined];

const isCfSubjectSubCase = (value: unknown): value is CfSubjectSubCase =>
  CF_SUBJECT_SUB_CASES.includes(value as CfSubjectSubCase);

const cfReferralOpeningOf = (value: unknown): NexusReferralCfOpening | undefined => {
  const { fromStep, domain, subject } = (value ?? {}) as Record<string, unknown>;
  const { domain: subjectDomain, subCase } = (subject ?? {}) as Record<string, unknown>;
  const namesAKnownStep = isCfNexusFromStep(fromStep);
  const domainIsAbsentOrString = domain === undefined || isString(domain);
  const wellFormedSubject: CfEventSubject | undefined =
    isString(subjectDomain) && isCfSubjectSubCase(subCase)
      ? { domain: subjectDomain, ...(subCase !== undefined && { subCase }) }
      : undefined;
  const subjectIsAbsentOrWellFormed = subject === undefined || wellFormedSubject !== undefined;

  return namesAKnownStep && domainIsAbsentOrString && subjectIsAbsentOrWellFormed
    ? { fromStep, ...(domain !== undefined && { domain }), ...(wellFormedSubject && { subject: wellFormedSubject }) }
    : undefined;
};

export function nexusReferralOf(state: unknown): NexusReferral | undefined {
  const { nexusReferralCode, newConversation, cfOpening } = (state ?? {}) as Partial<
    Record<keyof NexusReferral, unknown>
  >;

  return isNexusEntryPoint(nexusReferralCode)
    ? nexusReferralState(nexusReferralCode, {
        newConversation: newConversation === true,
        cfOpening: cfReferralOpeningOf(cfOpening),
      })
    : undefined;
}
